import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import { formatDate } from '../utils/helper';

interface VehicleTableProps {
  vehicles: any[];
  onEdit: (vehicle: any) => void;
  onDelete: (vehicleId: string) => void;
  onViewHistory: (vehicleId: string) => void;
}

export default function VehicleTable({
  vehicles,
  onEdit,
  onDelete,
  onViewHistory,
}: VehicleTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Calculate total service cost from history
  const getTotalServiceCost = (vehicle: any) => {
    if (vehicle.history && vehicle.history.length > 0) {
      return vehicle.history.reduce((sum: number, service: any) => {
        return sum + (service.totalServiceCost || service.amountOfParts || service.totalPartsCost || 0);
      }, 0);
    }
    return 0;
  };

  // Get last service date from history
  const getLastServiceDate = (vehicle: any) => {
    if (vehicle.history && vehicle.history.length > 0) {
      // Sort by date descending and get the first
      const sorted = [...vehicle.history].sort((a, b) =>
        new Date(b.spareChangedDate).getTime() - new Date(a.spareChangedDate).getTime()
      );
      return sorted[0]?.spareChangedDate;
    }
    return vehicle.lastService;
  };

  // Get latest odo reading from history
  const getLatestOdo = (vehicle: any) => {
    if (vehicle.history && vehicle.history.length > 0) {
      const sorted = [...vehicle.history].sort((a, b) =>
        (b.odoReading || 0) - (a.odoReading || 0)
      );
      return sorted[0]?.odoReading;
    }
    return vehicle.odo;
  };

  // Mobile card view - for phones and tablets
  if (isMobile || isTablet) {
    return (
      <Box sx={{ width: '100%' }}>
        {vehicles.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No vehicles found
            </Typography>
          </Paper>
        ) : (
          vehicles.map((vehicle) => (
            <Paper
              key={vehicle._id}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              }}
            >
              {/* Header with vehicle number and actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141' }}>
                    {vehicle.number || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {vehicle.transport?.transportName || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={vehicle.history?.length ? `View Service History (${vehicle.history?.length || 0} services)` : 'No services'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          if (vehicle.history?.length) {
                            onViewHistory(vehicle._id);
                          } else {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        sx={{
                          color: vehicle.history?.length ? 'var(--primary-color)' : '#ccc',
                          pointerEvents: vehicle.history?.length ? 'auto' : 'none',
                          cursor: vehicle.history?.length ? 'pointer' : 'default',
                        }}
                        disabled={!vehicle.history?.length}
                      >
                        <Badge badgeContent={vehicle.history?.length || 0} color="primary" max={99}>
                          <HistoryIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit Vehicle">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(vehicle)}
                      sx={{ color: 'var(--primary-color)' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Vehicle">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(vehicle._id)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Vehicle details grid - 2 columns for tablet, 1 for mobile */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: isTablet ? '1fr 1fr 1fr' : '1fr 1fr',
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Last Service</Typography>
                  <Typography variant="body2">{formatDate(getLastServiceDate(vehicle)) || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">ODO</Typography>
                  <Typography variant="body2">{getLatestOdo(vehicle)?.toLocaleString() || vehicle.odo?.toLocaleString() || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Next Service</Typography>
                  <Typography variant="body2">{formatDate(vehicle.nextServiceDue) || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Oil Change</Typography>
                  <Typography variant="body2">{vehicle.nextOilChangeOdo?.toLocaleString() || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Greasing</Typography>
                  <Typography variant="body2">{vehicle.nextGreasingOdo?.toLocaleString() || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Total Spent</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#062141' }}>
                    ₹{getTotalServiceCost(vehicle).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        overflowX: 'auto',
      }}
    >
      <Table sx={{ minWidth: 1100 }} aria-label="vehicle table">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#062141' }}>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Vehicle No.</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Transport Name</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Last Service</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>ODO</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Next Service</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Oil Change</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Greasing</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Spent</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicles.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No vehicles found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            vehicles.map((vehicle) => (
              <TableRow
                key={vehicle._id}
                sx={{
                  '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                  '&:hover': { backgroundColor: '#e3f2fd' },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                  {vehicle.number || 'N/A'}
                </TableCell>
                <TableCell>{vehicle.transport?.transportName || 'N/A'}</TableCell>
                <TableCell>{formatDate(getLastServiceDate(vehicle)) || 'N/A'}</TableCell>
                <TableCell>{getLatestOdo(vehicle)?.toLocaleString() || vehicle.odo?.toLocaleString() || 'N/A'}</TableCell>
                <TableCell>{formatDate(vehicle.nextServiceDue) || 'N/A'}</TableCell>
                <TableCell>{vehicle.nextOilChangeOdo?.toLocaleString() || 'N/A'}</TableCell>
                <TableCell>{vehicle.nextGreasingOdo?.toLocaleString() || 'N/A'}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>₹{getTotalServiceCost(vehicle).toLocaleString()}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title={vehicle.history?.length ? `View Service History (${vehicle.history?.length || 0} services)` : 'No services'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            if (vehicle.history?.length) {
                              onViewHistory(vehicle._id);
                            } else {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          sx={{
                            color: vehicle.history?.length ? 'var(--primary-color)' : '#ccc',
                            pointerEvents: vehicle.history?.length ? 'auto' : 'none',
                            cursor: vehicle.history?.length ? 'pointer' : 'default',
                          }}
                          disabled={!vehicle.history?.length}
                        >
                          <Badge badgeContent={vehicle.history?.length || 0} color="primary" max={99}>
                            <HistoryIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit Vehicle">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(vehicle)}
                        sx={{ color: 'var(--primary-color)' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Vehicle">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(vehicle._id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
